import React, { useMemo } from 'react';
import { Drawer, message } from 'antd';
import {
  STATUS_COLOR,
  STATUS_LABEL,
  timeAgo,
  effectiveSince,
  agingBucket,
} from '../lib/status.js';
import { parseName } from '../lib/format.js';
import { useOnuHistory } from '../hooks/useOnuHistory.js';
import { OpticalSparkline } from './OpticalSparkline.jsx';

const labelStyle = {
  fontSize: 11,
  color: '#6b7280',
  textTransform: 'uppercase',
  marginTop: 14,
  letterSpacing: 0.5,
  fontWeight: 600,
};
const valueStyle = { fontSize: 14, color: '#111827', marginTop: 3 };

function formatDuration(s) {
  if (s == null || s < 0) return '—';
  if (s < 60) return `${s}s`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}min`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h${m % 60 ? ` ${m % 60}min` : ''}`;
  const d = Math.floor(h / 24);
  return `${d}d${h % 24 ? ` ${h % 24}h` : ''}`;
}

function TimelineRow({ ev }) {
  const toColor = STATUS_COLOR[ev.to_status] || '#9ca3af';
  return (
    <li style={{ display: 'flex', gap: 10, padding: '8px 0', borderBottom: '1px solid #f3f4f6' }}>
      <span
        style={{
          flex: '0 0 8px',
          width: 8,
          height: 8,
          marginTop: 6,
          borderRadius: '50%',
          background: toColor,
        }}
      />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 13, color: '#111827' }}>
          {ev.from_status_label ? (
            <>
              <span style={{ color: STATUS_COLOR[ev.from_status] || '#6b7280' }}>
                {ev.from_status_label}
              </span>
              <span style={{ color: '#9ca3af', margin: '0 6px' }}>→</span>
            </>
          ) : (
            <span style={{ color: '#9ca3af', fontSize: 11, marginRight: 6 }}>
              (primeira observação)
            </span>
          )}
          <strong style={{ color: toColor }}>{ev.to_status_label}</strong>
        </div>
        <div style={{ fontSize: 11, color: '#6b7280', marginTop: 2 }}>
          {new Date(ev.observed_at).toLocaleString('pt-BR')}
          {ev.duration_in_from_seconds != null && (
            <>
              {' '}· permaneceu <strong>{formatDuration(ev.duration_in_from_seconds)}</strong>
            </>
          )}
          {ev.source && ev.source !== 'poller_snmp' && (
            <span style={{ marginLeft: 6, color: '#9ca3af', fontSize: 10 }}>({ev.source})</span>
          )}
        </div>
      </div>
    </li>
  );
}

function Timeline({ sn }) {
  const { data: events = [], isLoading, error } = useOnuHistory(sn, { days: 15 });

  if (isLoading) {
    return <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0' }}>carregando histórico…</div>;
  }
  if (error) {
    return (
      <div style={{ fontSize: 12, color: '#ef4444', padding: '8px 0' }}>
        Erro carregando histórico ({String(error.message || error)})
      </div>
    );
  }
  if (!events.length) {
    return (
      <div style={{ fontSize: 12, color: '#9ca3af', padding: '8px 0' }}>
        Sem transições registradas nos últimos 15 dias.
      </div>
    );
  }
  return (
    <ul style={{ listStyle: 'none', padding: 0, margin: '4px 0 0', maxHeight: 240, overflowY: 'auto' }}>
      {events.map((ev, i) => (
        <TimelineRow key={`${ev.observed_at}-${i}`} ev={ev} />
      ))}
    </ul>
  );
}

function FieldRow({ label, value, copyable, onCopy }) {
  return (
    <>
      <div style={{ ...labelStyle, display: 'flex', alignItems: 'center', gap: 8 }}>
        {label}
        {copyable && (
          <button
            type="button"
            onClick={onCopy}
            style={{
              border: 'none',
              background: 'transparent',
              fontSize: 10,
              color: '#3b82f6',
              cursor: 'pointer',
              padding: 0,
              textTransform: 'none',
              letterSpacing: 0,
            }}
          >
            copiar
          </button>
        )}
      </div>
      <div style={valueStyle}>{value || '—'}</div>
    </>
  );
}

export function ONUDrawer({ onu, onClose }) {
  const open = !!onu?.onu_sn;
  const name = useMemo(() => parseName(onu?.person_name), [onu?.person_name]);
  const since = onu ? effectiveSince(onu) : null;
  const bucket = onu ? agingBucket(onu) : null;

  function copy(text, label) {
    if (!text) return;
    navigator.clipboard?.writeText(String(text)).then(
      () => message.success(`${label} copiado`),
      () => message.error('Erro ao copiar')
    );
  }

  if (!onu) return null;

  const statusColor = STATUS_COLOR[onu.status] || '#9ca3af';
  const statusLbl = STATUS_LABEL[onu.status] || 'unknown';
  const slot = onu.slot ?? onu.slot_olt;
  const port = onu.port ?? onu.port_olt;
  const lastSeen = onu.last_seen_at || onu.observed_at;

  return (
    <Drawer
      title={
        <div style={{ display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: 16, color: '#111827', fontWeight: 600 }}>
            {name.display || 'Sem cadastro'}
          </div>
          {name.subtitle && (
            <div style={{ fontSize: 11, color: '#6b7280', fontWeight: 400, marginTop: 2 }}>
              {name.subtitle}
            </div>
          )}
        </div>
      }
      placement="right"
      width={420}
      onClose={onClose}
      open={open}
    >
      {/* Status badge + aging com bucket dot + ground truth marker */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            padding: '4px 12px',
            borderRadius: 16,
            background: `${statusColor}1a`,
            color: statusColor,
            fontSize: 12,
            fontWeight: 700,
          }}
        >
          <span
            style={{
              width: 8,
              height: 8,
              borderRadius: '50%',
              background: statusColor,
              boxShadow: `0 0 0 2px ${statusColor}33`,
            }}
          />
          {statusLbl}
        </span>
        <span
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 12,
            color: bucket ? bucket.color : '#374151',
            fontWeight: bucket ? 600 : 400,
          }}
        >
          {bucket && (
            <span
              style={{
                width: 8,
                height: 8,
                borderRadius: '50%',
                background: bucket.color,
              }}
            />
          )}
          há <strong>{timeAgo(since)}</strong>
        </span>
        {onu.last_change_at_olt && (
          <span
            style={{ fontSize: 10, color: '#9ca3af' }}
            title="Timestamp vindo do SNMP .46.1.23 da OLT (sobrevive a restart do poller)"
          >
            (ground truth OLT)
          </span>
        )}
      </div>

      <FieldRow
        label="Contrato"
        value={onu.contract_code || onu.contract_id}
        copyable={!!(onu.contract_code || onu.contract_id)}
        onCopy={() => copy(onu.contract_code || onu.contract_id, 'Contrato')}
      />

      <FieldRow label="Modelo ONT" value={onu.onu_model} />

      <FieldRow
        label="Serial ONU"
        value={<code style={{ fontSize: 13 }}>{onu.onu_sn}</code>}
        copyable
        onCopy={() => copy(onu.onu_sn, 'SN')}
      />

      <FieldRow
        label="OLT"
        value={
          <>
            {onu.olt_name || onu.olt_code || '—'}{' '}
            <span style={{ color: '#9ca3af', fontSize: 12 }}>(id {onu.olt_id ?? '—'})</span>
          </>
        }
      />

      <FieldRow
        label="Posição GPON"
        value={
          slot != null && port != null ? (
            <>
              slot <strong>{slot}</strong> / porta <strong>{port}</strong>
            </>
          ) : (
            '—'
          )
        }
      />

      <FieldRow
        label="Sinal óptico"
        value={
          <>
            RX <strong>{onu.signal_rx_dbm ?? '—'}</strong> dBm{' '}
            <span style={{ color: '#9ca3af' }}>·</span> TX{' '}
            <strong>{onu.signal_tx_dbm ?? '—'}</strong> dBm
          </>
        }
      />

      <FieldRow label="CEP" value={onu.postal_code} />

      <FieldRow
        label="Coordenadas"
        value={
          onu.install_lat != null && onu.install_lng != null ? (
            <>
              {Number(onu.install_lat).toFixed(5)}, {Number(onu.install_lng).toFixed(5)}
              {onu.geo_quality && (
                <span style={{ fontSize: 11, color: '#9ca3af' }}> ({onu.geo_quality})</span>
              )}
              {' '}
              <a
                href={`https://www.google.com/maps?q=${onu.install_lat},${onu.install_lng}`}
                target="_blank"
                rel="noreferrer"
                style={{ fontSize: 11, marginLeft: 6 }}
              >
                abrir no Maps ↗
              </a>
            </>
          ) : (
            '—'
          )
        }
      />

      <FieldRow
        label="Última leitura"
        value={lastSeen ? new Date(lastSeen).toLocaleString('pt-BR') : '—'}
      />

      {onu.pppoe_user && (
        <FieldRow label="PPPoE user" value={<code>{onu.pppoe_user}</code>} />
      )}

      <div style={labelStyle}>Tendência óptica</div>
      <OpticalSparkline sn={onu.onu_sn} />

      <div style={labelStyle}>Histórico (15d)</div>
      <Timeline sn={onu.onu_sn} />

      {/* Nome cru — só mostra se diferente do limpo (debug útil sem poluir) */}
      {name.original && name.original !== name.display && (
        <details style={{ marginTop: 16 }}>
          <summary style={{ ...labelStyle, marginTop: 0, cursor: 'pointer' }}>
            nome cru no Voalle
          </summary>
          <div
            style={{
              fontSize: 11,
              color: '#6b7280',
              marginTop: 6,
              padding: 8,
              background: '#f9fafb',
              borderRadius: 4,
              wordBreak: 'break-all',
              fontFamily: 'monospace',
            }}
          >
            {name.original}
          </div>
        </details>
      )}
    </Drawer>
  );
}
